import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useCenterSupplierCalendar } from 'hooks/useCenterSupplierCalendar';
import { useGridEdit } from 'hooks/useGridEdit'
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { convertDateToRequestParam, isEqualDate } from 'utils/convertFromLocalDate'
import { validateRequired } from 'utils/validation'

import DuplicateCenterDialog from '../../../dialog/DuplicateCenterDialog/DuplicateCenterDialog'

import type { ColDef, ValueFormatterParams, ValueParserParams, IRichCellEditorParams, GetContextMenuItemsParams, MenuItemDef, DefaultMenuItem } from 'ag-grid-enterprise'
import type { MstCenter, MstSupplier, Codes } from 'api-client';
import type { GetMstCalendarCenterSupplierCalendarCenterSupplierGetRequest, MstCalendarCenterSupplierSeq, MstCalendarCenterSuppliersSeq } from 'hooks/useCenterSupplierCalendar'
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
  "org_center_id": "orgCenterId",
  "org_supplier_id": "orgSupplierId",
  "calendar_date": "calendarDate",
  "enable_supplier_shipday": "enableSupplierShipday",
  "enable_orderday": "enableOrderday",
  "arrival_ratio": "arrivalRatio",
  "remarks": "remarks",
  "delete_flg": "deleteFlg"
};

const primayKey = ['orgCenterId', 'orgSupplierId', 'calendarDate']

const tableFieldToGroup: Record<string, string> = {
  enableSupplierShipday: 'extend_enable_flg',
  enableOrderday: 'extend_enable_flg',
}

interface DataListProps extends DataListPropsBase {
  list: MstCalendarCenterSuppliersSeq
  onReload: () => void
  searchParam: GetMstCalendarCenterSupplierCalendarCenterSupplierGetRequest
  center: MstCenter[]
  supplier: MstSupplier[]
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

// Row type: enable fields use typeCode (string) from extend_enable_flg (same as MasterCalender)
type MstCalendarCenterSupplierWithId =
  Omit<MstCalendarCenterSupplierSeq, 'id' | 'enableSupplierShipday' | 'enableOrderday'> &
  RecordEditEx & {
    enableSupplierShipday?: string | null
    enableOrderday?: string | null
  }

function DataList({ 
  list, 
  isLoading, 
  searchParam, 
  center,
  supplier,
  onReload, 
  updateTrigger,
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: DataListProps) {
  const { loginUserInfo, typeCode } = useAuthContext()
  const { fetchCalendarUpdate, result, isUpdating } = useCenterSupplierCalendar()
  const internalGridRef = useRef<AgGridReact<MstCalendarCenterSupplierWithId>>(null)
  const gridRef = externalGridRef || internalGridRef
  const searchParamCurrent = useRef<GetMstCalendarCenterSupplierCalendarCenterSupplierGetRequest>(searchParam)

  const defaultEnableFlag = typeCode.find((r) => r.typeGroup === 'extend_enable_flg')?.codes.find((r) => r.defaultFlg)?.typeCode || null

  // Normalize boolean from API to typeCode (string) for extend_enable_flg dropdown
  const extendEnableFlgCodes = useMemo(() => {
    const group = typeCode?.find((r) => r.typeGroup === 'extend_enable_flg')
    const codes = group?.codes || []
    const enabledCode = codes.find((c) => c.defaultFlg)?.typeCode ?? codes[0]?.typeCode ?? '1'
    const disabledCode = codes.find((c) => !c.defaultFlg && c.typeCode !== enabledCode)?.typeCode ?? codes[1]?.typeCode ?? '0'
    return { enabledCode, disabledCode }
  }, [typeCode])

  useEffect(() => {
    searchParamCurrent.current = searchParam
  }, [searchParam])

  
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const [editorDataReady, setEditorDataReady] = useState(false);
  const [row, setRow] = useState<MstCalendarCenterSupplierWithId[]>(
    list.list.map((item) => ({ ...item, ...initRecordEditEx() } as MstCalendarCenterSupplierWithId))
  )
  const [centerSelect, setCenterSelect] = useState<(string | null)[]>([])
  const [supplierSelect, setSupplierSelect] = useState<(string | null)[]>([])
  const [orderSysTypeCodeLists, setOrderSysTypeCodeLists] = useState<{ [key: string]: Codes[] }>({})
  const [orderSysTypeCodeSelects, setOrderSysTypeCodeSelects] = useState<{ [key: string]: (string | null)[] }>({})
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const [selectedRowForDuplicate, setSelectedRowForDuplicate] = useState<MstCalendarCenterSupplierWithId | null>(null)
  const [selectedCenterIds, setSelectedCenterIds] = useState<string[]>([])

  useEffect(() => {
    setResultMessage(result)
  }, [result])
  
  useEffect(() => {
    const newLists: { [key: string]: Codes[] } = {}
    const newSelects: { [key: string]: (string | null)[] } = {}

    const extendEnableFlgGroup = typeCode?.find((r) => r.typeGroup === 'extend_enable_flg')
    const codes = extendEnableFlgGroup?.codes || []
    newLists.extend_enable_flg = codes
    newSelects.extend_enable_flg = codes.map((r) => r.typeCode)

    setOrderSysTypeCodeLists(newLists)
    setOrderSysTypeCodeSelects(newSelects)
  }, [typeCode])

  useEffect(() => {
    if (centerSelect.length > 0 && supplierSelect.length > 0 && orderSysTypeCodeSelects.extend_enable_flg?.length > 0) {
      setEditorDataReady(true);
    }
  }, [centerSelect, supplierSelect, orderSysTypeCodeSelects]);
  
  useEffect(() => {
    setEditorDataReady(false)
    const centerValues = center?.map((r) => r.orgCenterId) || []
    setCenterSelect([...centerValues])
    const supplierValues = supplier?.map((r) => r.orgSupplierId) || []
    setSupplierSelect([...supplierValues])
  }, [center, supplier]);

  const createBlankRow = useCallback((): MstCalendarCenterSupplierWithId => {
    const currentCenter = center?.find(centerExist => centerExist.orgCenterId === searchParamCurrent.current.orgCenterId);
    const currentOrgCenterId = currentCenter?.orgCenterId || null;
    const currentSupplier = supplier?.find(supplierExist => supplierExist.orgSupplierId === searchParamCurrent.current.orgSupplierId);
    const currentOrgSupplierId = currentSupplier?.orgSupplierId || null;
  
    return {
      companyId: loginUserInfo?.companyId || null,
      orgCenterId: currentOrgCenterId,
      orgSupplierId: currentOrgSupplierId,
      calendarDate: null,
      enableSupplierShipday: defaultEnableFlag,
      enableOrderday: defaultEnableFlag,
      deleteDatetime: null,
      updateUser: null,
      deleteUser: null,
      insertDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      updateDatetime: null,
      arrivalRatio: null,
      remarks: null,
      deleteFlg: false,
      ...initRecordEditEx(true)
    };
  }, [searchParam, center, supplier, loginUserInfo?.companyId, loginUserInfo?.userId]);
  
  // 入力チェック
  const fieldValidate = (pRow: MstCalendarCenterSupplierWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    switch (field){
      case "orgCenterId":
      case "orgSupplierId":
      case "calendarDate":
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue));
        if (!hasErrors) {
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break;

      default:
        return false;
    }
    return hasErrors
  };
  
  const updateMstCalendar = useCallback(
    async (updatedRows: MstCalendarCenterSupplierWithId[]) => {
    const datalist = updatedRows.map(({ isEdited, orgCenterId, orgSupplierId, calendarDate, editedFields, errors, id, ...rest }) => ({
        ...rest,
        orgCenterId: orgCenterId ?? null,
        orgSupplierId: orgSupplierId ?? null,
        calendarDate: calendarDate ? convertDateToRequestParam(new Date(calendarDate)) : null,
      }));

    try {
      await fetchCalendarUpdate({
        mstCalendarCenterSupplierUpdate: {
          body: datalist as any
        }
      })
      onReload()
    } catch (err: any) {
      if (err?.errors && err.result?.status === "error") {
        const gridApi = gridRef.current?.api
        err.errors.forEach((errrow: any) => {
          const {rowdata}=errrow
          const orgCenterId = (rowdata as { org_center_id: string | null })?.org_center_id;
          const orgSupplierId = (rowdata as { org_supplier_id: string | null })?.org_supplier_id;
          const calendarDate = (rowdata as { calendar_date: string | null})?.calendar_date;
          const errRow = updatedRows.find((rowdatas) => 
            rowdatas.orgCenterId === orgCenterId && 
            rowdatas.orgSupplierId === orgSupplierId &&
            isEqualDate(rowdatas.calendarDate, calendarDate)
          ); 
          if (errRow) {
            // UpdateAPIのErrorは、PKをエラー表記して、サーバーから取得したエラーを表示する
            errRow.errors = {
              orgCenterId: [errrow.errorMessage],
              orgSupplierId: [errrow.errorMessage],
              calendarDate: [errrow.errorMessage],
            }
            const rowNode = gridApi?.getRowNode(errRow.id || '')
            if (rowNode) {
              rowNode.setData(errRow)
            }
          }
        })
      }
    }
  }, [fetchCalendarUpdate, searchParam]);

  const handleImportExcel = (data: any[]) => {
    let hasError = false;
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstCalendarCenterSupplierWithId> = {};
  
      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping];
          const importvalue = excelRow[key];
          let setvalue = importvalue || null;
          if (['deleteFlg'].indexOf(tableField) >= 0) {
            setvalue = parseBoolean(importvalue)
          } else if ([
            'enableSupplierShipday',
            'enableOrderday',
          ].indexOf(tableField) >= 0) {
            const group = tableFieldToGroup[tableField]
            const typeList = orderSysTypeCodeLists[group]
            const codevalue = typeCodeParser(importvalue, typeList)
            if (isNonInputValue(importvalue)) {
              setvalue = null
            } else if (codevalue) {
              setvalue = codevalue
            } else {
              setvalue = parseBoolean(importvalue)
            }
          } else if (tableField === 'calendarDate') {
            setvalue = parseDate(importvalue) as unknown as undefined;
          } else if (tableField === 'arrivalRatio') {
            setvalue = parseNumericWithRangeZero(importvalue);
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null;
          }
          // パース出来ないときはスキップ（＝NULL）し、警告メッセージを表示
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true;
          mappedRow[tableField as keyof MstCalendarCenterSupplierWithId] = setvalue as unknown as undefined
        }
      });
      return mappedRow as MstCalendarCenterSupplierWithId;
    });
    return { mappedData, hasError }
  };

  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    numberValueParserZero,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parsePositiveIntegerZero,
    parseStringValue,
    parseTime,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    dateTreeSetFilterParams,
    clearPinnedRows,
    getContextMenuItems: baseGetContextMenuItems,
    numberRangeValueParserZero,
    parseNumericWithRangeZero,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    storageKey: 'gridColumnState:/master-center-supplier-calendar',
    updateData: updateMstCalendar,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    scrollPositionKey: 'orgCenterId',
    rowData: row,
  })

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef)[] => 
  [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: '自社センターID',
      field: 'orgCenterId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.isNewRecord),
      },
      width: 300,
      flex:3,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) => selectValueFormatter(params.value, center, "orgCenterId", "centerName"),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgCenterId, center, "orgCenterId", "centerName"),
      valueParser: (params: ValueParserParams) => selectValueParser(params, center, "orgCenterId"),
      cellEditorParams: editorDataReady ? {
        values : [null, ...centerSelect],
        formatValue: (value: string) => selectValueFormatter(value, center, "orgCenterId", "centerName"),
        searchType: "match",
        allowTyping: true,
        filterList: true,
        highlightMatch: true,
      } as IRichCellEditorParams : {values: ['-']},
    },
    {
      headerName: '仕入先ID',
      field: 'orgSupplierId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.isNewRecord),
      },
      width: 300,
      flex:3,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) => selectValueFormatter(params.value, supplier, "orgSupplierId", "supplierName"),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgSupplierId, supplier, "orgSupplierId", "supplierName"),
      valueParser: (params: ValueParserParams) => selectValueParser(params, supplier, "orgSupplierId"),
      cellEditorParams: editorDataReady ? {
        values : [null, ...supplierSelect],
        formatValue: (value: string) => selectValueFormatter(value, supplier, "orgSupplierId", "supplierName"),
        searchType: "match",
        allowTyping: true,
        filterList: true,
        highlightMatch: true,
      } as IRichCellEditorParams : {values: ['-']},
    },
    {
      headerName: '日付',
      field: 'calendarDate',
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {filter: 'agDateColumnFilter',
            filterParams: {
              comparator: (filterLocalDateAtMidnight: Date, cellValue: any) => {
                const d = parseDate(cellValue)
                if (!d) return -1
                const cell = new Date(d.getFullYear(), d.getMonth(), d.getDate())
                const filter = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                )
                if (cell < filter) return -1
                if (cell > filter) return 1
                return 0
              },
            },
          },
          {
            filter: 'agSetColumnFilter',
            filterParams: dateTreeSetFilterParams,
          },
        ],
        closeOnApply: true,
      },
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => params.data && params.data.isNewRecord, 
      },
      width: 200,
      flex:2,
      cellEditor: 'agDateCellEditor',
      cellEditorParams: {
        minYear: 1900, 
        maxYear: 2050,
      },
      valueFormatter: dateFormatter, 
      valueParser: parseDateValue,
    },
    {
      headerName: '仕入先の納入可能',
      field: 'enableSupplierShipday',
      editable: isEnableEdit(),
      flex:2,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        typecodeFormatter(
          (params.value !== null && params.value !== undefined) ? String(params.value) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      filterValueGetter: (params) =>
        typecodeFormatter(
          (params.data.enableSupplierShipday !== null && params.data.enableSupplierShipday !== undefined) ? String(params.data.enableSupplierShipday) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      valueParser: (params: ValueParserParams) =>
        selectTypecodeValueParser(params, orderSysTypeCodeLists.extend_enable_flg || []),
      cellEditorParams: editorDataReady ? {
        values : [null, ...(orderSysTypeCodeSelects.extend_enable_flg || [])],
        formatValue: (value: string) =>
          typecodeFormatter(value, orderSysTypeCodeLists.extend_enable_flg || []),
      } as IRichCellEditorParams : {values: ['-']},
      cellClassRules: getCellClassRules(),
    },
    {
      headerName: '受注受付可能',
      field: 'enableOrderday',
      editable: isEnableEdit(),
      flex:2,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        typecodeFormatter(
          (params.value !== null && params.value !== undefined) ? String(params.value) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      filterValueGetter: (params) =>
        typecodeFormatter(
          (params.data.enableOrderday !== null && params.data.enableOrderday !== undefined) ? String(params.data.enableOrderday) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      valueParser: (params: ValueParserParams) =>
        selectTypecodeValueParser(params, orderSysTypeCodeLists.extend_enable_flg || []),
      cellEditorParams: editorDataReady ? {
        values : [null, ...(orderSysTypeCodeSelects.extend_enable_flg || [])],
        formatValue: (value: string) =>
          typecodeFormatter(value, orderSysTypeCodeLists.extend_enable_flg || []),
      } as IRichCellEditorParams : {values: ['-']},
      cellClassRules: getCellClassRules(),
    },
    {
      headerName: '入荷比率設定',
      field: 'arrivalRatio',
      editable: isEnableEdit(),
      flex:2,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
      cellEditor:'agNumberCellEditor',
      valueFormatter: numberValueFormatter,
      valueParser: numberRangeValueParserZero, 
    },
    {
      headerName: '備考',
      field: 'remarks', 
      flex: 2,
      editable: isEnableEdit(),
      cellEditorPopup: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: longTextEditorParams,
      valueFormatter: longTextValueFormatter,
      valueParser: longTextValueParser,
      valueSetter: longTextValueSetter,
    },
    {
      headerName: '削除',
      field: 'deleteFlg',
      editable: isEnableEdit(),
      flex:1,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueFormatter: formatBooleanValue,
      valueParser: parseBooleanValue, 
    },
  ];
  const columnDefs = useMemo<(ColDef)[]>(updateColumnDefs, [editorDataReady, orderSysTypeCodeLists, orderSysTypeCodeSelects])

  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
        { headerName: 'PSI企業ID', children: [{ headerName: 'company_id', field: 'companyId' }] },
        { headerName: '自社センターID', children: [{ headerName: 'org_center_id', field: 'orgCenterId', cellClass: 'stringType' }] },
        { headerName: '仕入先ID', children: [{ headerName: 'org_supplier_id', field: 'orgSupplierId', cellClass: 'stringType' }] },
        { headerName: '日付', children: [{ headerName: 'calendar_date', field: 'calendarDate' }] },
        { headerName: '仕入先の納入可能', children: [{ headerName: 'enable_supplier_shipday', field: 'enableSupplierShipday', cellClass: 'stringType' }] },
        { headerName: '受注受付可能', children: [{ headerName: 'enable_orderday', field: 'enableOrderday', cellClass: 'stringType' }] },
        { headerName: '入荷比率設定', children: [{ headerName: 'arrival_ratio', field: 'arrivalRatio', cellClass: 'numberType' }] },
        { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
        { headerName: '削除', children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }] },
      ])
  }, [setDownloadColumnDefs])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow()
      setRow([blankRow])
    } else {
      setRow(list.list.map((item) => {
        const rowItem = { ...item, ...initRecordEditEx() } as MstCalendarCenterSupplierWithId
        if (typeof (item as any).enableSupplierShipday === 'boolean') {
          rowItem.enableSupplierShipday = (item as any).enableSupplierShipday
            ? extendEnableFlgCodes.enabledCode
            : extendEnableFlgCodes.disabledCode
        }
        if (typeof (item as any).enableOrderday === 'boolean') {
          rowItem.enableOrderday = (item as any).enableOrderday
            ? extendEnableFlgCodes.enabledCode
            : extendEnableFlgCodes.disabledCode
        }
        return rowItem
      }))
      clearPinnedRows()
    }
  }, [list.list, createBlankRow, extendEnableFlgCodes]);

  // available centers
  const availableCenters = useMemo(() => {
    if (!center || !selectedRowForDuplicate) {
      return center || []
    }
    return center.filter(c => c.orgCenterId !== selectedRowForDuplicate.orgCenterId)
  }, [center, selectedRowForDuplicate])

  // open duplicate dialog
  const handleOpenDuplicateDialog = useCallback((rowData: MstCalendarCenterSupplierWithId) => {
    setSelectedRowForDuplicate(rowData)
    setSelectedCenterIds([])
    setDuplicateDialogOpen(true)
  }, [])

  // close duplicate dialog
  const handleCloseDuplicateDialog = useCallback(() => {
    setDuplicateDialogOpen(false)
    setSelectedRowForDuplicate(null)
    setSelectedCenterIds([])
  }, [])

  // select all centers
  const handleSelectAllCenters = useCallback((checked: boolean) => {
    if (!availableCenters || availableCenters.length === 0) return
    if (checked) {
      const allCenterIds = availableCenters.map(c => c.orgCenterId || '').filter(id => id !== '')
      setSelectedCenterIds(allCenterIds)
    } else {
      setSelectedCenterIds([])
    }
  }, [availableCenters])

  // toggle select one center
  const handleToggleCenter = useCallback((centerId: string) => {
    setSelectedCenterIds(prev => {
      if (prev.includes(centerId)) {
        return prev.filter(id => id !== centerId)
      }
      return [...prev, centerId]
    })
  }, [])

  // duplicate record to new center
  const handleDuplicateToCenter = useCallback(() => {
    if (!selectedRowForDuplicate || selectedCenterIds.length === 0 || !gridRef.current) {
      return
    }

    const { api } = gridRef.current
    if (!api) return

    // create new records, one record for each selected center
    const { id, ...restData } = selectedRowForDuplicate
    const newRows: MstCalendarCenterSupplierWithId[] = selectedCenterIds.map(centerId => ({
      ...restData,
      orgCenterId: centerId,
      orgSupplierId: selectedRowForDuplicate.orgSupplierId,
      calendarDate: selectedRowForDuplicate.calendarDate,
      companyId: loginUserInfo?.companyId || null,
      insertUser: loginUserInfo?.userId || null,
      insertDatetime: null,
      updateUser: null,
      updateDatetime: null,
      deleteUser: null,
      deleteDatetime: null,
      ...initRecordEditEx(true),
      isEdited: true,
      editedFields: new Set(Object.values(columnMapping)),
      errors: {},
    }))

    // add all new rows to grid
    api.applyTransaction({ add: newRows })
    setHasGridEdited(true)
    handleCloseDuplicateDialog()
  }, [selectedRowForDuplicate, selectedCenterIds, gridRef, loginUserInfo, setHasGridEdited, handleCloseDuplicateDialog])

  // custom context menu with duplicate item
  const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
    const baseMenu = baseGetContextMenuItems(params)
    
    const { node } = params
    if (!node || !node.data) {
      return baseMenu
    }

    const rowData = node.data as MstCalendarCenterSupplierWithId
    
    // add duplicate item after last separator
    const duplicateMenuItem: MenuItemDef = {
      name: '選択した設定を他のセンターに複製',
      action: () => handleOpenDuplicateDialog(rowData),
      disabled: !isEnableEdit() || !rowData.orgCenterId || !rowData.orgSupplierId || !rowData.calendarDate,
    }

    // find last separator index and insert new item before it
    const lastSeparatorIndex = baseMenu.lastIndexOf('separator')
    if (lastSeparatorIndex >= 0) {
      const newMenu = [...baseMenu]
      newMenu.splice(lastSeparatorIndex, 0, duplicateMenuItem, 'separator')
      return newMenu
    }

    // if no separator, add to the end
    return [...baseMenu, 'separator', duplicateMenuItem]
  }, [baseGetContextMenuItems, handleOpenDuplicateDialog, isEnableEdit])

  return (
    <>
      <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column', height: '69vh' }}>
        <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
          <div style={gridStyle} className="ag-theme-balham">
            <AgGridReact
              key={`${row[0]?.companyId}-${editorDataReady}`}
              rowData={row}
              ref={gridRef}
              getRowId={(params) => params.data.id}
              columnDefs={columnDefs}
              onGridReady={onGridReady}
              localeText={localeText}
              loadingOverlayComponent={LoadingOverlay}
              findSearchValue={externalFindSearchValue}
              onFindChanged={externalOnFindChanged}
              getContextMenuItems={getContextMenuItems}
              stopEditingWhenCellsLoseFocus
              />
          </div>
        </div>
      </div>

      <DuplicateCenterDialog
        open={duplicateDialogOpen}
        onClose={handleCloseDuplicateDialog}
        onConfirm={handleDuplicateToCenter}
        availableCenters={availableCenters}
        selectedCenterIds={selectedCenterIds}
        onSelectAll={handleSelectAllCenters}
        onToggleCenter={handleToggleCenter}
        selectedRow={selectedRowForDuplicate}
        formatCenter={(centerId) => selectValueFormatter(centerId, center, "orgCenterId", "centerName")}
        formatDate={(dateValue) => dateFormatter({ value: dateValue } as any)}
      />
    </>
  );
}

export default DataList;

